
import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import {
  FiUser, FiMail, FiPhone, FiLock, FiBriefcase, FiCalendar, FiEyeOff, FiEye,
  FiTag, FiCheckCircle, FiRotateCw, FiUserPlus, FiShield, FiGlobe, FiChevronRight,
  FiChevronLeft, FiDollarSign, FiMapPin, FiFileText, FiSettings, FiLayers, FiCreditCard
} from "react-icons/fi";
import {
  FaRupeeSign, FaDollarSign, FaEuroSign, FaPoundSign, FaIdCard
} from "react-icons/fa";
import { GiMoneyStack } from "react-icons/gi";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../../Constants";
import { getAccessToken } from "../../token";
import 'react-toastify/dist/ReactToastify.css';

const AccountForm = () => {
  const { user } = useContext(AuthContext); // Keeping context for fallback, but logic uses token
  const token = getAccessToken();
  const [activeTab, setActiveTab] = useState(1);

  // --- LOGIC CHANGE START: Decode JWT to get Role ---
  const [currentUserRole, setCurrentUserRole] = useState("");

  useEffect(() => {
    if (token) {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);
        
        // Assuming the key in your JWT is 'role'. Adjust if it is 'accountType'.
        setCurrentUserRole(decoded.role || decoded.accountType); 
      } catch (e) {
        console.error("Failed to decode token", e);
      }
    }
  }, [token]);
  // --- LOGIC CHANGE END ---
// Standard Email Regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Standard Indian Mobile Number (10 digits starting with 6-9). 
// Note: You can adjust this if you want to allow international formats with '+'
const PHONE_REGEX = /^[6-9]\d{9}$/; 

// Standard Indian PAN Card Regex (5 Letters, 4 Digits, 1 Letter)
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i; 

// Standard Indian GST Regex (2 Digits, 5 Letters, 4 Digits, 1 Letter, 1 Alphanumeric, 'Z', 1 Alphanumeric)
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i;
  const [formData, setFormData] = useState({
    accountType: "", 
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    companyName: "",
    companyType: "",
    gstNumber: "",
    panNumber: "",
    address: "",
    customerType: "", 
    campaignType: "BOTH",
    creditDeductionBy: "",
    allowFromIp: "",
    validFrom: "",
    validTo: "",
    currency: "INR",
    costPerSms: "0.7", // Default to match your payload example
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currencyOptions = [
    { value: "INR", label: "INR", icon: FaRupeeSign },
    { value: "USD", label: "USD", icon: FaDollarSign },
    { value: "EUR", label: "EUR", icon: FaEuroSign },
    { value: "GBP", label: "GBP", icon: FaPoundSign },
    { value: "AED", label: "AED", icon: GiMoneyStack },
  ];

  const companyTypeOptions = [
    { value: "PRIVATE_LIMITED", label: "Private Limited" },
    { value: "PUBLIC_LIMITED", label: "Public Limited" },
    { value: "LLP", label: "LLP" },
    { value: "PARTNERSHIP", label: "Partnership" },
    { value: "PROPRIETORSHIP", label: "Proprietorship" },
    { value: "OTHER", label: "Other" }
  ];

  const creditDeductionOptions = [
    { value: "SUBMISSION_BASED", label: "On Submission" },
    { value: "DELIVERY", label: "On Delivery" }
  ];

  const customerTypeOptions = [
    { value: "POSTPAID", label: "Postpaid" },
    { value: "PREPAID", label: "Prepaid" },
  ];

  // --- LOGIC CHANGE START: Hierarchy based on JWT Role ---
  const getAvailableAccountTypes = () => {
    if (!currentUserRole) return [];
    
    switch (currentUserRole) {
      case "SUPER_SUPER_ADMIN":
        return [
          { value: "SUPER_ADMIN", label: "Super Admin" },
          { value: "RESELLER", label: "Reseller" },
          { value: "COMPANY", label: "Company" },
        ];
      case "SUPER_ADMIN":
        return [
          { value: "RESELLER", label: "Reseller" },
          { value: "COMPANY", label: "Company" },
        ];
      case "RESELLER":
        return [{ value: "COMPANY", label: "Company" }];
      default:
        return []; // COMPANY or others cannot create users
    }
  };
  // --- LOGIC CHANGE END ---

  const availableAccountTypes = getAvailableAccountTypes();

  // If role is COMPANY or token invalid, don't show form (or return null as requested)
  if (currentUserRole === "COMPANY") return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

 const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // --- REGEX VALIDATION START ---
      
      // 1. Email Validation
      if (!EMAIL_REGEX.test(formData.email)) {
        toast.warning("Please enter a valid email address.");
        setIsSubmitting(false);
        return;
      }

      // 2. Phone Validation (assuming it's required or provided)
      if (formData.phone && !PHONE_REGEX.test(formData.phone.replace(/[\s+]/g, ''))) {
        // .replace() removes spaces and '+' so the user can type "+91 9876543210" safely
        toast.warning("Please enter a valid 10-digit phone number.");
        setIsSubmitting(false);
        return;
      }

      // 3. PAN Validation (if provided)
      if (formData.panNumber && !PAN_REGEX.test(formData.panNumber)) {
        toast.warning("Please enter a valid PAN Number (e.g., ABCDE1234F).");
        setIsSubmitting(false);
        return;
      }

      // 4. GST Validation (if provided)
      if (formData.gstNumber && !GST_REGEX.test(formData.gstNumber)) {
        toast.warning("Please enter a valid GST Number.");
        setIsSubmitting(false);
        return;
      }
      
      // --- REGEX VALIDATION END ---

      // Existing Date Validation
      if (formData.validFrom && formData.validTo) {
        if (new Date(formData.validTo) < new Date(formData.validFrom)) {
          toast.warning("Valid To date must be after Valid From date");
          setIsSubmitting(false);
          return;
        }
      }

      // Map to New API Payload
      const payload = {
        email: formData.email,
        password: formData.password,
        role: formData.accountType,
        smsRate: parseFloat(formData.costPerSms || 0),
        currency: formData.currency,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        address: formData.address,
        companyName: formData.companyName,
        companyType: formData.companyType,
        gstNumber: formData.gstNumber?.toUpperCase(), // Good practice to send these as uppercase
        panNumber: formData.panNumber?.toUpperCase(), // Good practice to send these as uppercase
        validFrom: formData.validFrom ? new Date(formData.validFrom).toISOString() : null,
        validTo: formData.validTo ? new Date(formData.validTo).toISOString() : null
      };

      // API Call
      await axios.post(`${API_URL}/api/v1/users/create`, payload, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      toast.success("User created successfully!");
      handleReset();
      setActiveTab(1);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to create user. Please check inputs.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      accountType: "", firstName: "", lastName: "", email: "", phone: "", password: "",
      validFrom: "", validTo: "", customerType: "", companyName: "", companyType: "",
      gstNumber: "", panNumber: "", address: "", currency: "INR", costPerSms: "0.7",
      campaignType: "BOTH", creditDeductionBy: "", allowFromIp: "",
    });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1: 
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 animate-fadeIn">
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiLayers className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Account Type
              </label>
              <div className="relative">
                <select name="accountType" value={formData.accountType} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm appearance-none shadow-sm cursor-pointer">
                  <option value="">Select Role</option>
                  {availableAccountTypes.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400"><FiChevronRight className="w-4 h-4 rotate-90" /></div>
              </div>
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiUser className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> First Name
              </label>
              <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiUser className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Last Name
              </label>
              <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiMail className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Email
              </label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="name@company.com" required
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiPhone className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Phone
              </label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 98765 43210"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiLock className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Password
              </label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} placeholder="••••••••" required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
                <button type="button" onClick={togglePasswordVisibility} className="absolute right-3 top-2.5 text-gray-400 hover:text-[#0C4550]">
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiCalendar className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Valid From
              </label>
              <input type="date" name="validFrom" value={formData.validFrom} onChange={handleChange} required min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiCalendar className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Valid To
              </label>
              <input type="date" name="validTo" value={formData.validTo} onChange={handleChange} required min={formData.validFrom || new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 animate-fadeIn">
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiBriefcase className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Company Name
              </label>
              <input name="companyName" value={formData.companyName} onChange={handleChange} placeholder="Tech Solutions Ltd" required
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiFileText className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Company Type
              </label>
              <div className="relative">
                <select name="companyType" value={formData.companyType} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm appearance-none shadow-sm cursor-pointer">
                  <option value="">Select Entity</option>
                  {companyTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400"><FiChevronRight className="w-4 h-4 rotate-90" /></div>
              </div>
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiTag className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Customer Type
              </label>
              <div className="relative">
                <select name="customerType" value={formData.customerType} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm appearance-none shadow-sm cursor-pointer">
                  <option value="">Select Type</option>
                  {customerTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400"><FiChevronRight className="w-4 h-4 rotate-90" /></div>
              </div>
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiGlobe className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Currency
              </label>
              <div className="relative">
                <select name="currency" value={formData.currency} onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm appearance-none shadow-sm cursor-pointer">
                  {currencyOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400"><FiChevronRight className="w-4 h-4 rotate-90" /></div>
              </div>
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiCreditCard className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Cost Per SMS
              </label>
              <div className="relative">
                <input type="number" name="costPerSms" value={formData.costPerSms} onChange={handleChange} placeholder="0.7" min="0" step="0.0001"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm pl-8" />
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                  {formData.currency === "INR" ? <FaRupeeSign className="h-3.5 w-3.5" /> : <FiDollarSign className="h-3.5 w-3.5" />}
                </div>
              </div>
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiDollarSign className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Credit Deduction
              </label>
              <div className="relative">
                <select name="creditDeductionBy" value={formData.creditDeductionBy} onChange={handleChange} required
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm appearance-none shadow-sm cursor-pointer">
                  <option value="">Select Method</option>
                  {creditDeductionOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none text-gray-400"><FiChevronRight className="w-4 h-4 rotate-90" /></div>
              </div>
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiShield className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> GST Number
              </label>
              <input name="gstNumber" value={formData.gstNumber} onChange={handleChange} placeholder="22AAAAA0000A1Z5"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
            </div>
            <div className="group">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FaIdCard className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> PAN Number
              </label>
              <input name="panNumber" value={formData.panNumber} onChange={handleChange} placeholder="ABCDE1234F"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm" />
            </div>
            <div className="group col-span-1 sm:col-span-2 lg:col-span-3">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center">
                <FiMapPin className="mr-1.5 h-3.5 w-3.5 text-[#0C4550]" /> Address
              </label>
              <textarea name="address" value={formData.address} onChange={handleChange} rows="2" placeholder="Full business address..."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:border-[#0C4550] focus:ring-1 focus:ring-[#0C4550] transition-all duration-200 text-sm shadow-sm resize-none" maxLength="500" />
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F0EFEB] font-sans p-3 sm:p-4 md:p-6 flex items-center justify-center">
      <div className="w-full max-w-4xl bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden border border-white/50">
        <div className="bg-gradient-to-r from-[#0C4550] to-[#0A3842] p-4 sm:p-6 md:p-7 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-full bg-gradient-to-l from-[#C5F637]/10 to-transparent pointer-events-none" />
          <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/10">
                <FiUserPlus className="h-5 w-5 text-[#C5F637]" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-white">Create New Account</h1>
                <p className="text-[#A7A08F] text-xs sm:text-sm mt-0.5">Configure account details and billing</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-semibold bg-white/10 px-3 py-1.5 rounded-full border border-white/10 backdrop-blur-md mt-2 md:mt-0">
              <span className={activeTab >= 1 ? "text-[#C5F637]" : "text-gray-400"}>Identity</span>
              <span className="text-gray-500">/</span>
              <span className={activeTab >= 2 ? "text-[#C5F637]" : "text-gray-400"}>Business & Billing</span>
            </div>
          </div>
        </div>
        <div className="flex border-b border-gray-100 bg-gray-50/50 p-1.5 sm:p-2 gap-1.5 overflow-x-auto">
          <TabButton id={1} activeTab={activeTab} setActiveTab={setActiveTab} label="Identity & Access" icon={FiUser} subLabel="Basic Info"/>
          <TabButton id={2} activeTab={activeTab} setActiveTab={setActiveTab} label="Business & Billing" icon={FiBriefcase} subLabel="Company & Payments"/>
        </div>
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 md:p-7">
          {renderTabContent()}
          <div className="mt-6 flex justify-between items-center pt-4 border-t border-gray-100">
            <button type="button" onClick={() => setActiveTab(prev => Math.max(1, prev - 1))} disabled={activeTab === 1}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${activeTab === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-white hover:text-[#0C4550]'}`}>
              <FiChevronLeft size={14} /> Back
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={handleReset} className="flex items-center gap-1.5 px-4 py-2 rounded-lg font-semibold text-sm text-gray-500 hover:bg-gray-100 transition-all">
                <FiRotateCw size={12} /> Reset
              </button>
              {activeTab < 2 ? (
                <button type="button" onClick={() => setActiveTab(2)}
                  className="flex items-center gap-1.5 px-5 py-2 bg-[#0C4550] text-white rounded-lg font-bold text-sm hover:bg-[#09353d] shadow-md shadow-[#0C4550]/20 active:scale-95 transition-all">
                  Next <FiChevronRight size={14} />
                </button>
              ) : (
                <button type="submit" disabled={isSubmitting}
                  className="flex items-center gap-1.5 px-6 py-2 bg-[#C5F637] text-[#0C4550] rounded-lg font-bold text-sm hover:bg-[#b0e026] shadow-md shadow-[#C5F637]/20 active:scale-95 transition-all disabled:opacity-70 disabled:cursor-not-allowed">
                  {isSubmitting ? <><FiRotateCw className="animate-spin" size={14} /> Creating...</> : <><FiCheckCircle size={14} /> Create Account</>}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } } 
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

const TabButton = ({ id, activeTab, setActiveTab, label, subLabel, icon: Icon }) => {
  const isActive = activeTab === id;
  return (
    <button type="button" onClick={() => setActiveTab(id)}
      className={`flex-1 min-w-[160px] sm:min-w-[180px] flex items-center gap-2.5 p-2.5 sm:p-3 rounded-lg transition-all duration-300 border ${isActive ? "bg-white border-gray-200 shadow-sm ring-1 ring-[#0C4550]/5" : "bg-transparent border-transparent hover:bg-gray-100 opacity-60 hover:opacity-100"}`}>
      <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-md flex items-center justify-center transition-colors ${isActive ? "bg-[#0C4550] text-[#C5F637]" : "bg-gray-200 text-gray-500"}`}>
        <Icon size={18} />
      </div>
      <div className="text-left">
        <p className={`text-xs sm:text-sm font-bold ${isActive ? "text-[#0C4550]" : "text-gray-600"}`}>{label}</p>
        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{subLabel}</p>
      </div>
    </button>
  );
};

export default AccountForm;